// Prata: text chat over /api/chat SSE plus hold-to-talk voice through the
// voice-gateway WebSocket (profile phoenix.anchor). Half-duplex: the talk
// button is dead while the agent is hearing or answering, matching the
// gateway, which only accepts `start` from rest.
//
// Two honesty rules shape this screen. The talk button only exists while the
// socket is live — a grey dead button captioned "Håll för att prata" is a
// promise the app cannot keep, so it leaves the screen and comes back by
// itself when the quiet reconnect succeeds. And the voice is never a menu:
// the persona follows the clock (personaForNow), because asking a tired man
// which of five voices he wants is asking him to do our work.

import { streamChat, type ChatRequest } from "../api";
import { getToken } from "../config";
import { mountOrb } from "../pixel";
import { clear, el } from "../dom";
import { t } from "../strings";
import { VoiceClient, type VoicePhase } from "../voice";

type Persona = "anchor" | "asa" | "henri" | "night";

let sessionId = sessionStorage.getItem("phoenix.session") ?? "";
if (!sessionId) {
  sessionId = crypto.randomUUID();
  sessionStorage.setItem("phoenix.session", sessionId);
}

// Stockholm wall clock, same shape as idag.ts — the persona window has to match
// the schedule the server documents, not the phone's timezone.
function stockholmHour(): number {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  return Number(parts.find((p) => p.type === "hour")?.value ?? "0") % 24;
}

// Food words that hand midday over to Henri. Deliberately narrow, and matched
// on whole words so "tomat" and "klimat" stay out of the kitchen.
const FOOD_WORDS =
  /(^|[^a-zåäöé])(mat|maten|äta|äter|ät|åt|lagar?|recept|frukost|lunch|middag|mellanmål|smakar?|kök|koka|steka?|dryck|dricka|smoothie|aptit|protein)([^a-zåäöé]|$)/i;

// Persona by the clock, never a menu. Morning and the working day belong to
// Ankaret; midday hands over to Henri only when the turn is about food — his
// words, or a photo sent with nothing said, which is nearly always the plate.
// A photographed pill box at lunch is not a cooking session, so it stays with
// Ankaret. Evening is Åsa, late night is Natten.
function personaForNow(text: string, hasImage: boolean): Persona {
  const hour = stockholmHour();
  if (hour < 11) return "anchor";
  if (hour < 17) {
    return FOOD_WORDS.test(text) || (hasImage && text === "") ? "henri" : "anchor";
  }
  if (hour < 22) return "asa";
  return "night";
}

const PERSONAS: readonly Persona[] = ["anchor", "asa", "henri", "night"];

export function mountPrata(root: HTMLElement): () => void {
  // A task detail (e.g. the breathing exercise) can hand this visit to a
  // specific persona. One-shot: read, validate, clear — the clock rules again
  // on the next visit.
  const nextRaw = sessionStorage.getItem("phoenix.persona.next");
  sessionStorage.removeItem("phoenix.persona.next");
  const personaOverride: Persona | "john" | null =
    nextRaw === "john" || (PERSONAS as readonly string[]).includes(nextRaw ?? "")
      ? (nextRaw as Persona | "john")
      : null;

  clear(root);
  root.append(el("h1", { text: t("talk.title") }));

  const log = el("div", { class: "chat-log", "aria-live": "polite" });

  // The opening line is written here, on the device — it is app copy, not a
  // message Phoenix sent, so it is styled as an intro and never as a bubble.
  // It leaves as soon as there is a real turn to read.
  const starter = el("p", {
    class: "chat-starter",
    text: personaOverride === "john" ? t("prata2.starter.john") : t("prata2.starter"),
  });
  log.append(starter);

  // The talk button and its one state line travel together in a block that is
  // absent while the socket is down (see the note at the top of the file).
  const stateLine = el("div", { class: "voice-state", text: "" });
  const talkBtn = el("button", { class: "talk-btn", type: "button" });
  const talkLabel = el("span", { class: "talk-label", text: t("talk.hold") });
  talkBtn.disabled = true;
  const voiceBlock = el("div", { class: "voice-block" }, [talkBtn, stateLine]);
  voiceBlock.hidden = true;

  const input = el("input", {
    type: "text",
    placeholder: t("talk.placeholder"),
    autocomplete: "off",
    "aria-label": t("talk.placeholder"),
  });
  const sendBtn = el("button", { class: "btn primary", text: t("talk.send") });

  // Photo: one hidden file input behind four buttons — three labelled ones that
  // say what the picture is for, plus the free camera in the composer for
  // everything else. They share the capture -> downscale -> POST path; the only
  // difference is the question put in the box, which is what lets the
  // classifier tell food from medicine from a meter reading.
  const fileInput = el("input", {
    type: "file",
    accept: "image/*",
    class: "visually-hidden",
    "aria-label": t("prata2.photo.group"),
  }) as HTMLInputElement;
  const photoBtn = el("button", {
    type: "button",
    class: "btn photo-btn",
    text: `📷 ${t("talk.photo")}`,
    "aria-label": t("talk.photo"),
  });
  const photoActions = el("div", {
    class: "photo-actions",
    role: "group",
    "aria-label": t("prata2.photo.group"),
  });
  const photoPreview = el("div", { class: "photo-preview" });
  photoPreview.hidden = true;
  // Photo trouble is reported next to the composer, not in the voice line —
  // that line can be off screen with the rest of the voice block.
  const photoError = el("p", { class: "notice err", text: t("talk.photoError") });
  photoError.hidden = true;
  const form = el("form", { class: "chat-input" }, [photoBtn, input, sendBtn]);

  root.append(log, voiceBlock, photoActions, photoError, photoPreview, form, fileInput);

  // Mounted after insertion: the orb reads its palette from the CSS custom
  // properties, which only resolve once the node is in the document.
  const orb = mountOrb(talkBtn);
  orb.setPhase("off");
  talkBtn.append(talkLabel);

  let pendingImage: { base64: string; mediaType: string; dataUrl: string } | null = null;
  // The question waiting for the picture that is being taken. Empty for the
  // free camera, which lets him say it in his own words.
  let pendingAsk = "";

  function clearPendingImage(): void {
    pendingImage = null;
    pendingAsk = "";
    photoPreview.hidden = true;
    clear(photoPreview);
    fileInput.value = "";
  }

  function showPendingImage(): void {
    if (!pendingImage) return;
    clear(photoPreview);
    const img = el("img", { alt: t("talk.photo") }) as HTMLImageElement;
    img.src = pendingImage.dataUrl;
    const removeBtn = el("button", {
      type: "button",
      class: "btn",
      text: `✕ ${t("talk.photoRemove")}`,
    });
    removeBtn.addEventListener("click", clearPendingImage);
    photoPreview.append(img, removeBtn);
    photoPreview.hidden = false;
  }

  // Downscale to <=1280px JPEG: keeps uploads small and normalizes HEIC and
  // friends into a format the brain accepts.
  async function fileToJpeg(file: File): Promise<{ base64: string; dataUrl: string }> {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1280;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    return { base64: dataUrl.slice(dataUrl.indexOf(",") + 1), dataUrl };
  }

  function askForPhoto(ask: string): void {
    pendingAsk = ask;
    fileInput.click();
  }

  function photoAction(label: string, ask: string): HTMLButtonElement {
    const btn = el("button", {
      type: "button",
      class: "btn photo-action",
      text: `📷 ${label}`,
    });
    btn.addEventListener("click", () => askForPhoto(ask));
    return btn;
  }

  photoActions.append(
    photoAction(t("prata2.btn.food"), t("prata2.ask.food")),
    photoAction(t("prata2.btn.meds"), t("prata2.ask.meds")),
    photoAction(t("prata2.btn.reading"), t("prata2.ask.reading"))
  );

  photoBtn.addEventListener("click", () => askForPhoto(""));
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    photoError.hidden = true;
    fileToJpeg(file)
      .then(({ base64, dataUrl }) => {
        // The question goes in the box rather than behind his back: he reads
        // exactly what is about to be asked, and can change it or wipe it.
        if (pendingAsk && !input.value.trim()) input.value = pendingAsk;
        pendingImage = { base64, mediaType: "image/jpeg", dataUrl };
        showPendingImage();
      })
      .catch(() => {
        clearPendingImage();
        photoError.hidden = false;
      });
  });

  // ── bubbles ───────────────────────────────────────────────────────────────
  let agentBubble: HTMLElement | null = null;
  let pendingYou: HTMLElement | null = null;

  function addBubble(cls: string, text: string): HTMLElement {
    starter.remove(); // the opener is an intro, not a turn
    const b = el("div", { class: `bubble ${cls}`, text });
    log.append(b);
    log.scrollTop = log.scrollHeight;
    return b;
  }

  function appendAgent(piece: string): void {
    if (!agentBubble) agentBubble = addBubble("agent", "");
    agentBubble.textContent = (agentBubble.textContent ?? "") + piece;
    log.scrollTop = log.scrollHeight;
  }

  // ── voice ────────────────────────────────────────────────────────────────
  let voiceOk = false;

  // Half-duplex: the gateway silently drops a `start` while it is hearing or
  // answering, so the button must be dead in both phases — not just answering.
  // (It stays enabled during `listening`: that is the press itself, and a
  // disabled button would swallow the pointerup that sends the commit.)
  const phaseBlocked = (phase: VoicePhase): boolean =>
    phase === "hearing" || phase === "answering";

  const voice = new VoiceClient({
    onConnected(ok: boolean): void {
      voiceOk = ok;
      // No dead orb: the whole block leaves while the socket is down.
      // VoiceClient keeps retrying on its own, and this reappears when it wins.
      voiceBlock.hidden = !ok;
      talkBtn.disabled = !ok || phaseBlocked(voice.getPhase());
      orb.setPhase(ok ? voice.getPhase() : "off");
      if (!ok) stateLine.textContent = "";
    },
    onPhase(phase: VoicePhase): void {
      talkBtn.disabled = !voiceOk || phaseBlocked(phase);
      talkBtn.classList.toggle("rec", phase === "listening");
      orb.setPhase(voiceOk ? phase : "off");
      talkLabel.textContent = phase === "listening" ? t("talk.listening") : t("talk.hold");
      // One line for both machine phases — he does not need to know whether we
      // are transcribing or thinking, only that it is his turn to wait.
      stateLine.textContent = phaseBlocked(phase) ? t("prata2.voice.wait") : "";
      if (phase === "listening") pendingYou = null;
      if (phase === "answering") agentBubble = null;
    },
    onPartial(text: string): void {
      if (!pendingYou) pendingYou = addBubble("you pending", "");
      pendingYou.textContent = text;
      log.scrollTop = log.scrollHeight;
    },
    onFinal(text: string): void {
      if (text.trim()) {
        if (pendingYou) {
          pendingYou.textContent = text;
          pendingYou.classList.remove("pending");
        } else {
          addBubble("you", text);
        }
      } else if (pendingYou) {
        pendingYou.remove();
      }
      pendingYou = null;
    },
    onToken(piece: string): void {
      appendAgent(piece);
    },
    onReplyDone(): void {
      agentBubble = null;
    },
    onError(kind: string): void {
      stateLine.textContent = kind === "mic" ? t("talk.micDenied") : t("talk.error");
    },
  });

  voice.start();

  talkBtn.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    void voice.pressTalk();
  });
  talkBtn.addEventListener("pointerup", (e) => {
    e.preventDefault();
    voice.releaseTalk();
  });
  talkBtn.addEventListener("pointerleave", () => voice.releaseTalk());
  talkBtn.addEventListener("pointercancel", () => voice.releaseTalk());

  // ── text chat ────────────────────────────────────────────────────────────
  let sending = false;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    const image = pendingImage;
    if ((!text && !image) || sending) return; // a photo alone is a valid turn
    if (!getToken()) return; // the router never mounts tabs without a session
    input.value = "";
    const bubble = addBubble("you", text);
    if (image) {
      const img = el("img", { class: "bubble-photo", alt: t("talk.photo") }) as HTMLImageElement;
      img.src = image.dataUrl;
      bubble.prepend(img);
    }
    clearPendingImage();
    agentBubble = null;
    sending = true;
    sendBtn.disabled = true;
    // The persona rides along in the body — routes/api.ts reads `persona`, and
    // the shared ChatRequest type does not name the field yet.
    const payload: ChatRequest & { persona: Persona | "john" } = {
      message: text,
      session_id: sessionId,
      persona: personaOverride ?? personaForNow(text, Boolean(image)),
      ...(image ? { image_base64: image.base64, image_media_type: image.mediaType } : {}),
    };
    streamChat(payload, (piece) => appendAgent(piece))
      .catch(() => {
        addBubble("agent error", t("talk.error"));
      })
      .finally(() => {
        sending = false;
        sendBtn.disabled = false;
        agentBubble = null;
      });
  });

  return () => {
    orb.dispose();
    voice.stop();
  };
}
